import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import * as serializers from './serializers'

import * as requests from './requests'

import { calculateNextPageParam, serializeOptions } from '@cannect/services/common'
import { pickDocumentTabForProduct, pickProductCoverImagePath } from '@cannect/utils/productMedia'
import { TFlavor } from './types'

export const KEY_GET_PRODUCT_DETAILS = 'getProductDetail'
export const KEY_GET_PRODUCTS_FILTERS = 'getProductsFilters'
const KEY_GET_PRODUCTS_LIST = 'getProductsList'

const mapCuratorshipFilters = (filters: any) => {
  if (!filters) return {}

  return {
    ...filters,
    gmps: filters.gmp || filters.gmps || [],
    vehicles: filters.veiculo || filters.vehicles || [],
    extraction_methods: filters.extraction_method || filters.extraction_methods || []
  }
}

export const useGetProductById = (productId: number, enabled?: boolean) =>
  useQuery({
    queryKey: [KEY_GET_PRODUCT_DETAILS, productId],
    queryFn: () => requests.getProductById(productId),
    select: (data) => {
      const p = data?.product
      const classId = p?.class?.id ?? (p as { class_id?: number })?.class_id
      const docTab = pickDocumentTabForProduct(p?.media, classId)
      return {
        product: {
          ...p,
          medical_data: data?.medicalData,
          curatorship_original: data?.product?.curatorship,
          // find valid promotion and add a valid_promotion property
          ...serializers.findValidPromotion(data.product),
          // join medical data with product informations to create a technical information object
          general_informations: serializers.serializeProductGeneralInformation({
            ...data.product,
            ...data?.medicalData
          }),
          coa_url: docTab.url,
          documentTabLabel: docTab.label,
          showDocumentTab: docTab.show,
          // find recommended flavor and add a isRecomendedFlavor property
          flavors: data.product?.flavors?.map((flavor: TFlavor) => ({
            ...flavor,
            isRecommendedFlavor:
              serializers.findRecommendedFlavor(data.product?.flavors, data.product?.flavor) === flavor?.flavor
          })),
          curatorship: serializers.serializeCuratorship(data?.product?.curatorship ?? undefined)
        }
      }
    },
    meta: {
      errorMessage: 'Erro ao recuperar os dados do produto.'
    },
    enabled: (!!productId && enabled) ?? true,
    staleTime: Infinity
  })

export const useGetProductsFilters = () =>
  useQuery({
    queryKey: [KEY_GET_PRODUCTS_FILTERS],
    queryFn: () => requests.getProductsFilters(),
    select: (data) => ({
      brand_id: serializeOptions(data.filters.brand_id),
      shape_id: serializeOptions(data.filters.shape_id),
      class_id: serializeOptions(data.filters.class_id),
      cannabidiol_id: serializeOptions(data.filters.cannabidiol_id),
      cannabinoid_name: serializeOptions(data.filters.cannabinoid_name),
      volume_name: serializeOptions(data.filters.volume_name),
      proportion_id: serializeOptions(data.filters.proportion_id),
      max_price: serializeOptions(data.filters.max_price),
      cbd_concentration: serializeOptions(data.filters.cbd_concentration),
      thc_concentration: serializeOptions(data.filters.thc_concentration),
      category_id: serializeOptions(data.filters.category_id),
      // Filtros de curadoria para prescritores
      gmp: data.filters.gmp ? serializeOptions(data.filters.gmp) : [],
      veiculo: data.filters.veiculo ? serializeOptions(data.filters.veiculo) : [],
      extraction_method: data.filters.extraction_method ? serializeOptions(data.filters.extraction_method) : [],
      // Mapeamento para o formato esperado pelo componente
      gmps: data.filters.gmp ? serializeOptions(data.filters.gmp) : [],
      vehicles: data.filters.veiculo ? serializeOptions(data.filters.veiculo) : [],
      extraction_methods: data.filters.extraction_method ? serializeOptions(data.filters.extraction_method) : []
    }),
    meta: {
      errorMessage: 'Erro ao carregar os filtros.'
    },
    staleTime: Infinity
  })

export const useGetPublicProductsListInfiniteQuery = (queryParams: URLSearchParams, enabled?: boolean) => {
  return useInfiniteQuery({
    queryKey: [KEY_GET_PRODUCTS_LIST, 'public', queryParams.toString(), enabled],
    queryFn: ({ pageParam = 1 }) => {
      return requests.getPublicProductsList(pageParam.toString(), queryParams)
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        products: {
          ...page.products,
          rows: page.products.rows.map((product) => ({
            ...product,
            image: pickProductCoverImagePath(product?.media)
          })),
          filters: mapCuratorshipFilters(page.products.filters)
        }
      }))
    }),
    getNextPageParam: (lastPage, allPages) => {
      const itemsPerPage = Number(queryParams.get('limit') || 12)
      return calculateNextPageParam({
        currentPage: allPages[0].products.page || 1,
        totalItems: lastPage.products.count,
        itemsPerPage
      })
    },
    meta: {
      errorMessage: 'Houve um erro ao recuperar a listagem de produtos.'
    },
    staleTime: 1000 * 60 * 2,
    enabled: enabled ?? true,
    initialPageParam: 1
  })
}

export const useGetAuthenticatedProductsListInfiniteQuery = (queryParams: URLSearchParams, enabled?: boolean) => {
  return useInfiniteQuery({
    queryKey: [KEY_GET_PRODUCTS_LIST, 'authenticated', queryParams.toString(), enabled],
    queryFn: ({ pageParam = 1 }) => {
      return requests.getAuthenticatedProductsList(pageParam.toString(), queryParams)
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        products: {
          ...page.products,
          rows: page.products.rows.map((product) => ({
            ...product,
            ...serializers.findValidPromotion(product),
            image: pickProductCoverImagePath(product?.media),
            flavors: product?.flavors?.map((flavor: TFlavor) => ({
              ...flavor,
              isRecommendedFlavor:
                serializers.findRecommendedFlavor(product?.flavors, product?.flavor) === flavor?.flavor
            }))
          })),
          filters: mapCuratorshipFilters(page.products.filters)
        }
      }))
    }),
    getNextPageParam: (lastPage, allPages) => {
      const itemsPerPage = Number(queryParams.get('limit') || 12)
      return calculateNextPageParam({
        currentPage: allPages.length,
        totalItems: lastPage.products.count,
        itemsPerPage
      })
    },
    meta: {
      errorMessage: 'Houve um erro ao recuperar a listagem de produtos.'
    },
    staleTime: 1000 * 60 * 2,
    enabled: enabled ?? true,
    initialPageParam: 1
  })
}

// TODO Marketplace V2: Ver como vai retornar do backend

const KEY_GET_PRODUCTS_BY_BENEFIT = 'getProductsByBenefit'
const KEY_GET_WEEKLY_HIGHLIGHTS = 'getWeeklyHighlights'
const KEY_GET_MOST_SEARCHED_PRODUCTS = 'getMostSearchedProducts'
const KEY_GET_PRODUCTS_BY_SUPPLIER = 'getProductsBySupplier'
const KEY_GET_PRODUCTS_BY_CLASS = 'getProductsByClass'
const KEY_GET_PRODUCTS_BY_TAG = 'getProductsByTag'

export const useGetProductsByBenefit = (benefitCategory: string, queryParams?: URLSearchParams, enabled?: boolean) => {
  return useInfiniteQuery({
    queryKey: [KEY_GET_PRODUCTS_BY_BENEFIT, benefitCategory, queryParams?.toString(), 'public'],
    queryFn: ({ pageParam = 1 }) => {
      return requests.getProductsByBenefitPublic(benefitCategory, pageParam.toString(), queryParams)
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        products: {
          ...page.products,
          rows: page.products.rows.map((product) => ({
            ...product,
            ...serializers.findValidPromotion(product),
            image: pickProductCoverImagePath(product?.media),
            flavors: product?.flavors?.map((flavor: TFlavor) => ({
              ...flavor,
              isRecommendedFlavor:
                serializers.findRecommendedFlavor(product?.flavors, product?.flavor) === flavor?.flavor
            }))
          }))
        }
      }))
    }),
    getNextPageParam: (lastPage, allPages) => {
      const itemsPerPage = Number(queryParams?.get('limit') || 12)
      return calculateNextPageParam({
        currentPage: allPages.length,
        totalItems: lastPage.products.count,
        itemsPerPage
      })
    },
    meta: {
      errorMessage: `Erro ao recuperar produtos da categoria ${benefitCategory}.`
    },
    staleTime: 1000 * 60 * 5, // 5 minutes cache for benefit categories
    enabled: enabled ?? true,
    initialPageParam: 1
  })
}

export const useGetWeeklyHighlights = (limit?: number, enabled?: boolean, onlySellable?: boolean) => {
  return useQuery({
    queryKey: [KEY_GET_WEEKLY_HIGHLIGHTS, limit, 'public', !!onlySellable],
    queryFn: () => requests.getWeeklyHighlightsPublic(limit, { onlySellable }),
    select: (data) => ({
      ...data,
      products: (((data as any).products?.rows || (data as any).products) as any[]).map((product: any) => ({
        ...product,
        ...serializers.findValidPromotion(product),
        image: pickProductCoverImagePath(product?.media),
        flavors: product?.flavors?.map((flavor: TFlavor) => ({
          ...flavor,
          isRecommendedFlavor: serializers.findRecommendedFlavor(product?.flavors, product?.flavor) === flavor?.flavor
        }))
      }))
    }),
    meta: {
      errorMessage: 'Erro ao recuperar destaques da semana.'
    },
    staleTime: 1000 * 60 * 10, // 10 minutes cache for weekly highlights
    enabled: enabled ?? true
  })
}

export const useGetMostSearchedProducts = (limit?: number, enabled?: boolean) => {
  return useQuery({
    queryKey: [KEY_GET_MOST_SEARCHED_PRODUCTS, limit, 'public'],
    queryFn: () => requests.getMostSearchedProductsPublic(limit),
    select: (data) => ({
      ...data,
      products: (((data as any).products?.rows || (data as any).products) as any[]).map((product: any) => ({
        ...product,
        ...serializers.findValidPromotion(product),
        image: pickProductCoverImagePath(product?.media),
        flavors: product?.flavors?.map((flavor: TFlavor) => ({
          ...flavor,
          isRecommendedFlavor: serializers.findRecommendedFlavor(product?.flavors, product?.flavor) === flavor?.flavor
        }))
      }))
    }),
    meta: {
      errorMessage: 'Erro ao recuperar produtos mais buscados.'
    },
    staleTime: 1000 * 60 * 10, // 10 minutes cache for most searched products
    enabled: enabled ?? true
  })
}

export const useGetProductsBySupplier = (supplierId: number, limit?: number, enabled?: boolean) => {
  return useQuery({
    queryKey: [KEY_GET_PRODUCTS_BY_SUPPLIER, supplierId, limit, 'public'],
    queryFn: () => requests.getProductsBySupplierPublic(supplierId, limit),
    select: (data) => ({
      ...data,
      products: (((data as any).products?.rows || (data as any).products) as any[]).map((product: any) => ({
        ...product,
        ...serializers.findValidPromotion(product),
        image: pickProductCoverImagePath(product?.media),
        flavors: product?.flavors?.map((flavor: TFlavor) => ({
          ...flavor,
          isRecommendedFlavor: serializers.findRecommendedFlavor(product?.flavors, product?.flavor) === flavor?.flavor
        }))
      }))
    }),
    meta: {
      errorMessage: 'Erro ao recuperar produtos por fornecedor.'
    },
    staleTime: 1000 * 60 * 10, // 10 minutes cache for products by supplier
    enabled: enabled ?? true
  })
}

export const useGetProductsByClass = (classId: number, limit?: number, enabled?: boolean, onlySellable?: boolean) => {
  return useQuery({
    queryKey: [KEY_GET_PRODUCTS_BY_CLASS, classId, limit, 'public', !!onlySellable],
    queryFn: () => requests.getProductsByClassPublic(classId, limit, { onlySellable }),
    select: (data) => ({
      ...data,
      products: (((data as any).products?.rows || (data as any).products) as any[]).map((product: any) => ({
        ...product,
        ...serializers.findValidPromotion(product),
        image: pickProductCoverImagePath(product?.media),
        flavors: product?.flavors?.map((flavor: TFlavor) => ({
          ...flavor,
          isRecommendedFlavor: serializers.findRecommendedFlavor(product?.flavors, product?.flavor) === flavor?.flavor
        }))
      }))
    }),
    meta: {
      errorMessage: 'Erro ao recuperar produtos por classe.'
    },
    staleTime: 1000 * 60 * 10,
    enabled: enabled ?? true
  })
}

export const useGetProductsByTag = (tag: string, limit?: number, enabled?: boolean, onlySellable?: boolean) => {
  return useQuery({
    queryKey: [KEY_GET_PRODUCTS_BY_TAG, tag, limit, 'public', !!onlySellable],
    queryFn: () => requests.getProductsByTagPublic(tag, limit, { onlySellable }),
    select: (data) => ({
      ...data,
      products: (((data as any).products?.rows || (data as any).products) as any[]).map((product: any) => ({
        ...product,
        ...serializers.findValidPromotion(product),
        image: pickProductCoverImagePath(product?.media),
        flavors: product?.flavors?.map((flavor: TFlavor) => ({
          ...flavor,
          isRecommendedFlavor: serializers.findRecommendedFlavor(product?.flavors, product?.flavor) === flavor?.flavor
        }))
      }))
    }),
    meta: {
      errorMessage: 'Erro ao recuperar produtos por tag.'
    },
    staleTime: 1000 * 60 * 10,
    enabled: enabled ?? true
  })
}

const KEY_GET_PRODUCT_SUBCATEGORIES = 'getProductSubcategories'

export const useGetProductSubcategories = (enabled?: boolean) =>
  useQuery({
    queryKey: [KEY_GET_PRODUCT_SUBCATEGORIES],
    queryFn: () => requests.getProductSubcategories(),
    meta: {
      errorMessage: 'Erro ao recuperar as subcategorias de produtos.'
    },
    staleTime: 1000 * 60 * 10,
    enabled: enabled ?? true
  })

export const useToggleProductFavorite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (productId: number) => requests.toggleProductFavorite(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY_GET_PRODUCTS_LIST] })
      queryClient.invalidateQueries({ queryKey: [KEY_GET_PRODUCTS_BY_BENEFIT] })
      queryClient.invalidateQueries({ queryKey: [KEY_GET_WEEKLY_HIGHLIGHTS] })
      queryClient.invalidateQueries({ queryKey: [KEY_GET_MOST_SEARCHED_PRODUCTS] })
    },
    meta: {
      errorMessage: 'Erro ao favoritar produto.'
    }
  })
}
